import axios from 'axios';
import WorthyApiErrors from 'lib/api/worthyApiErrors';
import {
    googleApiKey,
    rootAPIUrl,
    rootUrl,
    segmentUrl,
    dataCollectorUrl,
    waveUrl,
    signNowServiceUrl,
    isProduction,
    getEnvName, authenticatorUrl
} from 'helpers/ApplicationHelper';
import User from 'lib/user';
import cookie from "react-cookie";

const CALENDLY_TOKEN = process.env.CALENDLY_TOKEN;

export const envName = getEnvName();
export const jwtName = () => (isProduction() ? 'jwt' : `${envName}_jwt`);

export default class WorthyApi {

    static _httpReq(method, url, params = {}, options = {}) {
        let promise = null;
        if (method === 'get') {
            const config = { withCredentials: true, ...options };
            promise = axios.get(url, config);
        } else {
            const config = {
                url,
                method,
                data: params,
                withCredentials: true, // default to true but allow override with options.
                ...options
            };
            promise = typeof axios === 'function' ? axios(config) : axios[method](url, params, options);
        }
        const req = promise
            .then((res) => {
                if (res.data.errors) {
                    throw new WorthyApiErrors(res);
                } else {
                    return res;
                }
            })
            .catch((res) => {
                if (res instanceof WorthyApiErrors) {
                    throw res; //propagate error
                } else {
                    throw new WorthyApiErrors(res);
                }
            });
        return req;
    }

    static _facebookCallback(url) {
        return this._httpReq('get', rootUrl(url));
    }

    static _get(url) {
        return this._httpReq('get', rootAPIUrl(url));
    }

    static _get_segment(url) {
        return this._httpReq('get', segmentUrl(url));
    }

    static _getCalendly(eventId) {
        const options = {
            url: `https://api.calendly.com/scheduled_events/${ eventId }`,
            headers: { Authorization: `Bearer ${ CALENDLY_TOKEN }` }
        };

        return axios.get(options.url, { headers: options.headers }).catch((err) => {
            console.error(JSON.stringify(err));
        });
    }

    static _post(url, params) {
        return this._httpReq('post', rootAPIUrl(url), params);
    }

    static _uploadFile(url, body, params) {
        body['extra_params'] = params;
        return this._httpReq('post', rootAPIUrl(url), body);
    }

    static _removeFile(url, path) {
        return this._httpReq('post', rootAPIUrl(url), { path });
    }

    static _patch(url, params) {
        return this._httpReq('patch', rootAPIUrl(url), params);
    }

    static _dataCollector(path, data) {
        const url = dataCollectorUrl(path);
        return this._httpReq('post', url, data, { withCredentials: false });
    }

    static _wave(method, path, data) {
        const url = waveUrl(path);
        return this._httpReq(method, url, data);
    }

    //// General

    static ado() {
        return this._get('/ado');
    }

    static getAddressFromGoogle(location) {
        let url = `https://maps.googleapis.com/maps/api/geocode/json?address=${ location.StreetLines },${ location.City },${ location.StateOrProvinceCode }&key=${ googleApiKey }`;
        return this._httpReq('get', url, {}, { withCredentials: false });
    }

    static sendAddressTypedEvent(itemId) {
        return this._get(`/item/${ itemId }/address_typed`);
    }

    //// end General

    //// Authentication

    static logIn({ email, password, bundle_id, request_id, otp_code }) {
        return this._post(
            '/sign_in',
            {
                email: email,
                password: password,
                bundle_id: bundle_id,
                request_id: request_id,
                otp_code: otp_code
            }
        );
    }

    static sendCode({ email, request_id }) {
        return this._post(
            '/send_otp_code',
            {
                email: email,
                request_id: request_id
            }
        );
    }

    static async logOut(id) {
        User.logOut();
        cookie.save(`${envName}_bridge_apptosite_logged_out`, 'true', {
            domain: 'worthy.com',
            path: '/',
            expires: new Date('2100-01-01'),
        });
        cookie.remove(jwtName(), { path: '/', domain: 'worthy.com' });
        if (id) {
            const url = authenticatorUrl(`seller/${id}/logout`);
            try { await this._httpReq('post', url, { withCredentials: true }); } catch (err) { /* no op */ }
        }
    }

    static register({first_name, last_name, email, phone, contact_preference, bundle_id, user_flow, optionalPhoneExp}) {
        let referer = User.loadReferer();
        let friendbuy_referer = User.loadRefererFriendbuy();
        return this._post(
            '/registration',
            {
                first_name,
                last_name,
                email,
                phone,
                referer,
                friendbuy_referer,
                contact_preference,
                bundle_id,
                user_flow,
                optional_phone_exp: optionalPhoneExp
            }
        );
    }

    static registerWithGoogle(credential) {
        return this._post(
            '/register_with_google',
            {
                credential
            }
        );
    }

    //// end Authentication

    //// User

    static setExperiments(userId) {
        return this._post('/user/' + userId + '/experiments');
    }

    static updateExperiments(userId, experiments) {
        return this._post(
            '/user/' + userId + '/experiments/update',
            { experiments }
        );
    }

    static requestShippingKit(publicId) {
        return this._get('/item/' + publicId + '/send_shipping_kit');
    }

    static helpCall({ name, phone, timeRange, timeZone, itemId }) {
        return this._post(
            '/user/get_help',
            {
                name: name,
                phone: phone,
                time_range: timeRange,
                time_zone: timeZone,
                item_id: itemId
            }
        );
    }

    static logUserAction(data) {
        switch (data.page) {
            case 'trends_page':
            case 'light_trends':
            case 'cta_button':
            case 'listing_page':
            case 'light_listing_page':
            case 'upload_photo':
            case 'hv_listing_page':
            case 'pending_auction':
            case 'quick_sale':
            case 'assorted_auction':
            case 'missing_details':
            case 'live_chat_HV':
            case 'support_negotiation':
                return this._post(
                    `/user_actions/item/${ data.itemId }/events`,
                    data
                );
            case 'payment_details':
            case 'my_account':
            case 'update_reserve_price':
            case 'drawer':
                return this._post(
                    `/user_actions/user/${ data.page }`,
                    data
                );
            case 'survey':
                return this._post(
                    `/user_actions/item/${ data.objectId }/${ data.page }`,
                    data
                );
            case 'log_by_email':
                return this._post(
                    `/user_actions/user/${ data.page }`,
                    data
                );
            case 'manage_call':
                return this._post(
                    `/user_actions/user/user_action`,
                    data
                );
            case 'manage_call':
                return this._post(
                    `/user_actions/user/user_action`,
                    data
                );
        }
    }

    static getPaymentDetails(itemId = null) {
        let url = '/user/payment_details';
        itemId && (url += `?item_id=${ itemId }`);
        return this._get(url);
    }

    static savePaymentDetails(data) {
        return this._patch(
            `/user/payment_details`,
            data
        );
    }

    static checkLead(data) {
        return this._post(
            '/user/check_lead',
            data
        );
    }

    static updateMissingDetails(data) {
        return this._post(
            '/user/update_missing_details',
            data
        );
    }

    static facebookLogIn(bundleId) {
        // return this._facbookCallback(
        //     `/auth/facebook/callback?signed_request=${signedRequest}`
        // );
        // let referer = User.loadReferer();
        // let friendbuy_referer = User.loadRefererFriendbuy();
        return this._facebookCallback(
            `/auth/facebook/callback?origin=app?bundle_id=${ bundleId }`
        );
    }

    // object_id: item public id OR jewelry-box (bundle) id
    static updateUserPhone(objectId, userId, phone, appointmentTime, is_bundle) {
        return this._post(
            `/user/update_phone`,
            {
                object_id: objectId,
                user_id: userId,
                phone,
                appointmentTime,
                is_bundle
            }
        );
    }

    static getCalendlyData(eventId) {
        return this._getCalendly(eventId);
    }

    //// end User

    //// My Account

    static getUserBundles(userId) {
        return this._get(`/my_account/${ userId }/user_bundles`);
    }

    static getUserCurrentItemsCount(userId) {
        return this._get(`/my_account/${ userId }/user_current_items_count`);
    }

    static getBundleWithItems(bundleId) {
        return this._get(`/my_account/${ bundleId }/bundle_with_items`);
    }

    static resetNotificationsCounter(bundleId) {
        return this._post(
            `/my_account/${ bundleId }/reset_notifications_counter`
        );
    }

    static acceptBundleSale(bundleId) {
        return this._post(
            `/my_account/${ bundleId }/accept_bundle_sale`
        );
    }

    //// end My Account

    //// Item

    static submitItem(params) {
        return this._post(
            '/item/submit_properties',
            params
        );
    }

    /**
     * @param {String} itemType
     * @param {Number} id
     * */
    static addDefaultItem(id, itemType) {
        return this._post(
            `/item/${ id }/add_another_item`,
            { itemType }
        );
    }

    static getItemByPublicID(id) {
        return this._get(
            `/item/${ id }`
        );
    }

    static setReschedulePickupAttempt(id) {
        return this._post(
            `/item/${ id }/reschedule_pickup_attempt`
        );
    }

    static generateItemEstimationByPublicID(id) {
        return this._post(
            `/item/${ id }/generate_estimation`
        );
    }

    static autoEstimateForCUI(id) {
        return this._post(
            `/item/${ id }/auto_estimate_for_cui`
        );
    }

    static acceptItemEstimationByPublicID(id) {
        return this._post(
            `/item/${ id }/accept_estimation`
        );
    }

    static generateItemShippingByPublicID(id, params) {
        return this._post(
            `/item/${ id }/create_shipping`,
            params
        );
    }

    static startAuction(id, params) {
        return this._post(
            `/item/${ id }/start_auction`,
            params
        );
    }

    static prepareStartAuction(id, params) {
        return this._post(
            `/item/${ id }/prepare_start_auction`,
            params
        );
    }

    static acceptSell(id) {
        return this._post(
            `/item/${ id }/user_accepted_sell`
        );
    }

    static setNegotiationPrice(id, params) {
        return this._post(
            `/item/${ id }/set_negotiation_price`,
            params
        );
    }

    static handleDropOffSelected(itemId = null, dropOffLocations, expectFullResponse = false) {
        return this._post(
            `/item/${ itemId }/drop_off`,
            { drop_off_locations: dropOffLocations, expectFullResponse: expectFullResponse }
        );
    }

    static createShipmentMethodDetailsTask(itemId = null, taskSystemName) {
        return this._post(
            `/item/${ itemId }/update_shipment_method_details`,
            { task_system_name: taskSystemName }
        );
    }

    static shipItemBack(id, params) {
        return this._post(
            `/item/${ id }/user_refused_leading_bid`,
            params
        );
    }

    static setLastName(lastName) {
        return this._post(
            `/user/set_last_name`,
            { 'last_name': lastName }
        );
    }

    static userHasAnotherItem(userId) {
        return this._post(
            `/user/${ userId }/log_user_history`,
            {
                submodule: 'General',
                comment: 'Seller mentioned he has more valuables.',
                starred: true
            }
        );
    }

    static setItemChatBot(id) {
        return this._post(
            `/item/${ id }/set_chat_bot`
        );
    }

    static itemSaveLog(objectID, comment) {
        return this._post(
            `/item/${ objectID }/save_log`,
            { log_event: comment }
        );
    }

    static cuiSaveLog(objectID, objectType, message, version) {
        return this._post(
            `/item/${ objectID }/cui/save_log`,
            { object_type: objectType, message, version }
        );
    }

    static highValueSaveLog(version, objectID, comment, notifySlack) {
        return this._post(
            `/item/${ objectID }/high_value_page/save_log`,
            { comment, notifySlack, version }
        );
    }

    static hvCuiSaveLog(objectID, version, key, message, last_key, system) {
        return this._post(
            `/item/${ objectID }/hv_cui/save_log`,
            { message, version, key, system, last_key }
        );
    }

    static updateShipmentPreferences(itemId, preferences) {
        return this._post(
            `/item/${ itemId }/update_preferences`,
            { preferences }
        );
    }

    static schedulePickup(itemId, readyTimestamp, companyCloseTime, dropOffLocations, expectFullResponse = false) {
        return this._post(
            `/item/${ itemId }/schedule_pickup`,
            {
                ready_timestamp: readyTimestamp,
                company_close_time: companyCloseTime,
                drop_off_locations: dropOffLocations,
                expectFullResponse: expectFullResponse
            }
        );
    }

    static updateBuyItNowPrice(objectID, price, message, version) {
        return this._post(
            `/item/${ objectID }/cui/update_buy_it_now_price`,
            { price, message, version }
        );
    }

    static acceptSpecialSale(itemId) {
        return this._post(`/item/${ itemId }/user_accepted_pending_sale`);
    }

    static refuseSpecialSale(itemId) {
        return this._post(`/item/${ itemId }/user_refused_pending_sale`);
    }

    static cancelShipBackRequest(itemId) {
        return this._post(`/item/${ itemId }/cancel_ship_back_request`);
    }

    static getItemTrendsByPublicID(id) {
        return this._get(`/item/${ id }/trends`);
    }

    static getItemAppointment = (itemId) => {
        return this._get(`/appointment/get_user_item_appointment?item_id=${ itemId }`);
    }

    static getWorthyNumbers(id) {
        return this._get(`/item/${ id }/worthy_numbers`);
    }

    static updateCertificateNumber(itemId, certificateNumber) {
        return this._post(
            `/item/${ itemId }/update_certificate_number`,
            {
                certificate_number: certificateNumber
            }
        );
    }

    static updateCertificate(itemId, certificateNumber, certificateProvider = 'gia') {
        return this._post(
            `/item/${itemId}/update_certificate_data`,
            {
                data: {
                    certificate: certificateProvider,
                    certificate_number: certificateNumber,
                }
            }
        )
    }

    static updateCaratWeight(itemId, caratWeight) {
        return this._post(
            `/item/${ itemId }/update_carat_weight`,
            {
                carat_weight: caratWeight
            }
        );
    }

    static updateProperty(itemId, name, value, isGemstone = false) {
        return this._post(
            `/item/${ itemId }/update_property`,
            {
                name,
                value,
                gemstone: isGemstone
            }
        );
    }

    static updateGIACertificate(itemId) {
        return this._get(
            `/item/${ itemId }/update_certificate`
        );
    }

    static handleContactAE(itemId, task) {
        return this._get(
            `/item/${ itemId }/contact_ae?task=${ task }`
        );
    }

    static updateReservePrice(itemId, newReservePrice) {
        return this._post(
            `/item/${ itemId }/update_reserve_price`,
            { newReservePrice }
        );
    }

    static getListingTrendsAndLog(id, isLight = false) {
        return this._get(`/item/${ id }/listing_trends?lightTrendsPresented=${ isLight }`);
    }

    static abortItem(id) {
        return this._get(`/item/${ id }/online_abort_item`);
    }

    static getLastPreDeliveredItem(id) {
        return this._get(`/user/${ id }/last_pre_delivered_item`);
    }

    static async generateOfficeDropOffShipment(itemId, params){
        return this._post(`/item/${ itemId }/create_office_drop_off_shipment`, {
            date_time: params.dateTime,
            office_id: params.officeId,
            office_key: params.officeKey
        });
    }

    //// end Item

    //// Jewelry Box

    static createJewelryBox(subsets) {
        return this._post('/jewelry_box/create_jewelry_box', { subsets });
    }

    static jewelryBoxSaveLog(bundleId, version, key, message, system, to = null) {
        return this._post(
            `/jewelry_box/${ bundleId }/save_log`,
            { version, key, message, system, to }
        );
    }

    static getJewelryBoxUserData(bundleId) {
        return this._get(`/jewelry_box/${ bundleId }/bundle_info`);
    }

    static uploadImage(data, id, chatBotType) {
        // In this scenario the id is the bundle id
        return this._uploadFile(
            chatBotType === 'jewelryBox' ? `/jewelry_box/${ id }/upload_image` : `/item/${ id }/upload_image`,
            data
        );
    }

    static uploadImages(data, id) {
        return this._uploadFile(
            `/item/${ id }/upload_images`,
            data
        );
    }

    static removeImage(id, path, chatBotType) {
        return this._removeFile(
            `/${ chatBotType === 'jewelryBox' ? 'jewelry_box' : 'item' }/${ id }/remove_image`,
            path
        );
    }

    //// end Jewelry Box

    static refuseQuickSale(itemId) {
        return this._post(`/item/${ itemId }/user_refused_quick_sale`);
    }

    //// Appointment

    static scheduleAppointment(params) {
        return this._post(
            `/appointment/schedule_appointment`,
            params
        );
    }


    static cancelAppointment(id) {
        return this._post(`/appointment/cancel_appointment/${ id }`);
    }


    //// end Appointment

    /**
     *
     * @param url
     * @param method
     * @param params
     * @returns {*}
     */
    static makeCustomCall(url, method, params = null) {
        return this._httpReq(method || 'post', rootUrl(url), params);
    }

    /**
     *
     * @returns {*}
     */
    static getHomePageWorthStats() {
        let path = '/api/v2/get_worthy_stats';

        return this.makeCustomCall(path, 'get');
    }

    /**
     *
     * @param params
     * @returns {*}
     */
    static createNewsletterSubscribe(params) {
        let path = '/api/sellers/pre_lead/create_newsletter_subscribe';

        return this.makeCustomCall(path, 'post', params);
    }

    static getLiveAuctions(params = null) {
        let path = '/api/v2/get_live_auctions';

        return this.makeCustomCall(path, 'get', params);
    }

    /**
     *
     * @param params
     * @returns {*}
     */
    static getRecentDeals(params) {
        let path = '/api/v2/get_recent_deals';

        return this.makeCustomCall(path, 'post', params);
    }

    static getSegments(segmentedObjectParams) {
        const queryString = Object.keys(segmentedObjectParams).map(key => key + '=' + segmentedObjectParams[key]).join('&');
        return this._get_segment(`getSegments/?${ queryString }`);
    }

    static scheduleListingReminder(id, countDays) {
        return this._post(
            `/item/${ id }/schedule_listing_reminder`,
            { number: countDays }
        );
    }

    static checkIfLuxuryWatchSegmentExists(userId) {
        return this._get(
            `/user/${userId}/has_luxury_watch_segment`,
        );
    }

    static userLoggedIn(sessionIdentifier, userId) {
        return this._dataCollector('session_login', { sessionIdentifier, userId });
    }

    static getAppointmentUrl(url) {
        return rootAPIUrl(url);
    }

    // required params:
    // shipping_center_address
    // seller_address
    // address_updated
    // shipping_option
    // optional params:
    // email
    // otp_code
    // medium
    // request_id
    static postShipBackAddress(itemId, params) {
        return this._post(`/item/${ itemId }/shipback_confirmed`, params);
    }

    static getShippingCenterAddress(itemId, address) {
        return this._post(`/item/${ itemId }/get_shipping_center_locations`, { address });
    }

    static getSigningLink(params) {
        const { redirect_uri, firstname, lastname, contents, item_id, seller_id, return_address } = params;
        const url = signNowServiceUrl('createSigningLink');

        return this._httpReq('post', url, {
            redirect_uri,
            firstname,
            lastname,
            contents,
            item_id,
            seller_id,
            return_address,
        });
    }
}
